/**
 * FuelPanel - Main fuel management panel (Simulation Mode)
 * Tabbed interface for Spot Market, Contracts, and Hedging
 */

import React, { useState, useMemo } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Button } from "@/components/ui/button";
import {
  FUEL_SUPPLIERS,
  getPriceTrend,
  formatFuelPrice,
  formatGallons,
  getAvailableSuppliers,
} from "../../systems/FuelSystem.js";
import { FuelPriceChart } from "./FuelPriceChart.jsx";
import { SpotMarketTab } from "./SpotMarketTab.jsx";
import { ContractsTab } from "./ContractsTab.jsx";
import { HedgingTab } from "./HedgingTab.jsx";
import { SupplierNegotiation } from "./SupplierNegotiation.jsx";

// Shared styles
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  panel: {
    backgroundColor: "#0c0c0c",
    border: "1px solid #1a1a1a",
    borderRadius: "12px",
    width: "95%",
    maxWidth: "900px",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #1a1a1a",
    backgroundColor: "#0a0a0a",
  },
  headerTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#60ffd0",
    letterSpacing: "1px",
  },
  closeButton: {
    background: "transparent",
    border: "none",
    color: "#737373",
    fontSize: "20px",
    cursor: "pointer",
    padding: "4px 8px",
  },
  tabs: {
    display: "flex",
    borderBottom: "1px solid #1a1a1a",
    backgroundColor: "#0a0a0a",
  },
  tab: {
    flex: 1,
    padding: "12px 16px",
    background: "transparent",
    border: "none",
    color: "#737373",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    letterSpacing: "0.5px",
    borderBottom: "2px solid transparent",
    transition: "all 0.2s",
  },
  tabActive: {
    color: "#60ffd0",
    borderBottomColor: "#60ffd0",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
  },
  priceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#111",
    borderRadius: "8px",
    border: "1px solid #1a1a1a",
  },
};

const TABS = [
  { id: "spot", label: "SPOT MARKET" },
  { id: "contracts", label: "CONTRACTS" },
  { id: "hedging", label: "HEDGING" },
];

export function FuelPanel({ onClose }) {
  const { worldState, updateWorldState } = useWorldState();
  const [activeTab, setActiveTab] = useState("spot");
  const [showNegotiation, setShowNegotiation] = useState(null); // supplierId or null

  const fuelState = worldState?.fuel || {};
  const cash = worldState?.airline?.cash || 0;
  const macroState = worldState?.macro || {};
  const reputationTier = worldState?.tier || 1;

  const { change: priceChange, trend } = useMemo(
    () => getPriceTrend(fuelState),
    [fuelState]
  );

  const trendColor =
    trend === "up" ? "#ef4444" : trend === "down" ? "#22c55e" : "#737373";
  const trendIcon = trend === "up" ? "↑" : trend === "down" ? "↓" : "−";

  // Calculate today's fuel stats
  const todayStats = useMemo(() => {
    const totalPurchased = fuelState.totalFuelPurchased || 0;
    const avgPrice = fuelState.averagePurchasePrice || fuelState.spotPrice || 2.95;
    const contractSavings = fuelState.contractSavings || 0;
    const hedgeSavings = fuelState.hedgeSavings || 0;
    return {
      totalPurchased,
      avgPrice,
      totalSavings: contractSavings + hedgeSavings,
    };
  }, [fuelState]);

  // Active contracts count
  const activeContracts = useMemo(
    () => (fuelState.contracts || []).filter((c) => c.status === "active").length,
    [fuelState.contracts]
  );

  // Active hedges count
  const activeHedges = useMemo(
    () =>
      (fuelState.hedges || []).filter(
        (h) => h.status === "active" || h.status === "pending"
      ).length,
    [fuelState.hedges]
  );

  // Handle negotiation complete
  const handleNegotiationComplete = (contract) => {
    if (contract) {
      // Contract was created - update state
      updateWorldState((prev) => ({
        ...prev,
        fuel: {
          ...prev.fuel,
          contracts: [...(prev.fuel.contracts || []), contract],
          activeNegotiation: null,
        },
      }));
    }
    setShowNegotiation(null);
  };

  // If negotiation is active, show negotiation UI
  if (showNegotiation) {
    return (
      <SupplierNegotiation
        supplierId={showNegotiation}
        onComplete={handleNegotiationComplete}
        onCancel={() => setShowNegotiation(null)}
      />
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>FUEL MANAGEMENT</div>
          <button style={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Price Summary */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #1a1a1a" }}>
          <div style={styles.priceHeader}>
            <div>
              <div style={{ color: "#525252", fontSize: "12px", marginBottom: "4px" }}>
                CURRENT SPOT PRICE
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
                <span
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    fontFamily: "monospace",
                    color: "#60ffd0",
                  }}
                >
                  ${(fuelState.spotPrice || 2.95).toFixed(2)}
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: trendColor,
                  }}
                >
                  {trendIcon}${Math.abs(priceChange).toFixed(2)}/gal
                </span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#525252", fontSize: "12px", marginBottom: "4px" }}>
                YOUR FUEL COSTS
              </div>
              <div style={{ fontSize: "14px", color: "#e5e5e5" }}>
                Avg: ${todayStats.avgPrice.toFixed(2)}/gal
              </div>
              {todayStats.totalSavings > 0 && (
                <div style={{ fontSize: "13px", color: "#22c55e" }}>
                  Saved: ${(todayStats.totalSavings / 1000).toFixed(1)}K
                </div>
              )}
            </div>
          </div>

          {/* Quick stats */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#111",
                borderRadius: "6px",
                border: "1px solid #1a1a1a",
              }}
            >
              <div style={{ color: "#525252", fontSize: "11px" }}>CONTRACTS</div>
              <div style={{ color: "#e5e5e5", fontSize: "18px", fontWeight: "bold" }}>
                {activeContracts}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#111",
                borderRadius: "6px",
                border: "1px solid #1a1a1a",
              }}
            >
              <div style={{ color: "#525252", fontSize: "11px" }}>HEDGES</div>
              <div style={{ color: "#e5e5e5", fontSize: "18px", fontWeight: "bold" }}>
                {activeHedges}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#111",
                borderRadius: "6px",
                border: "1px solid #1a1a1a",
              }}
            >
              <div style={{ color: "#525252", fontSize: "11px" }}>CASH</div>
              <div style={{ color: "#22c55e", fontSize: "18px", fontWeight: "bold" }}>
                ${(cash / 1000000).toFixed(1)}M
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.content}>
          {activeTab === "spot" && (
            <SpotMarketTab
              fuelState={fuelState}
              macroState={macroState}
            />
          )}
          {activeTab === "contracts" && (
            <ContractsTab
              fuelState={fuelState}
              reputationTier={reputationTier}
              onStartNegotiation={(supplierId) => setShowNegotiation(supplierId)}
            />
          )}
          {activeTab === "hedging" && (
            <HedgingTab
              fuelState={fuelState}
              cash={cash}
              macroState={macroState}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default FuelPanel;
